import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import ChainSelect from '@/common/ChainSelect'
import CopyButtonWithLabel from '@/common/CopyButtonWithLabel'
import { downloadJson } from '@/helpers'
import ProposalPreview from '@/pages/Multisig/components/ProposalPreview'
import { useSubmitMultisigProposal } from '@/pages/Multisig/CreateProposal/hooks/submit-multisig-proposal'
import { UiButton } from '@/ui/UiButton'
import { UiInput } from '@/ui/UiInput'
import { UiLabel } from '@/ui/UiLabel'

import {
  buildDeleteChainMessage,
  DeleteChainFormValues,
} from './chainMessageBuilder'

const deleteChainSchema = z.object({
  creator: z.string().min(1, 'Creator address is required'),
  chainId: z.string().min(1, 'Chain ID is required'),
})

export default function DeleteChainForm() {
  const { account } = useParams<{ account: string }>()

  const form = useForm<DeleteChainFormValues>({
    resolver: zodResolver(deleteChainSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      creator: '',
      chainId: '',
    },
  })

  const submitProposalTx = useSubmitMultisigProposal({ group: account ?? '' })

  useEffect(() => {
    form.setValue('creator', account ?? '', { shouldValidate: true })
  }, [account, form])

  const watchedValues = useWatch({ control: form.control })

  const messagePayload = useMemo(() => {
    if (!form.formState.isValid || !watchedValues) return null

    try {
      return buildDeleteChainMessage(watchedValues as DeleteChainFormValues)
    } catch {
      return null
    }
  }, [form.formState.isValid, watchedValues])

  const generatedJson = useMemo(() => {
    if (!messagePayload) return null
    return JSON.stringify(messagePayload, null, 2)
  }, [messagePayload])

  const isSubmitting = submitProposalTx.isPending
  const isSubmitDisabled = !form.formState.isValid || isSubmitting

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <h3 className='text-foreground text-lg font-semibold'>
          Delete Chain Message
        </h3>
        <p className='text-muted-foreground text-sm'>
          Fill in the form to generate a message for deleting an existing chain
        </p>
      </div>

      <form onSubmit={event => event.preventDefault()} className='space-y-3'>
        <div className='space-y-1.5'>
          <UiLabel htmlFor='creator'>Creator Address</UiLabel>
          <UiInput
            id='creator'
            placeholder='Connect wallet'
            readOnly
            className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
            {...form.register('creator')}
          />
          {form.formState.errors.creator && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.creator.message}
            </p>
          )}
        </div>

        <div className='space-y-1.5'>
          <UiLabel>Select Chain</UiLabel>
          <ChainSelect
            control={form.control}
            name='chainId'
            onValueChange={(chainId: string) =>
              form.setValue('chainId', chainId, { shouldValidate: true })
            }
            disabled={isSubmitting}
            placeholder='Select a chain to delete'
          />
          {form.formState.errors.chainId && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.chainId.message}
            </p>
          )}
        </div>
      </form>

      {messagePayload && <ProposalPreview messages={messagePayload.messages} />}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2'>
          {generatedJson && (
            <>
              <CopyButtonWithLabel
                value={generatedJson}
                label='Copy JSON'
                copiedLabel='Copied'
                tooltip='Copy JSON'
              />
              <UiButton
                type='button'
                variant='outline'
                size='sm'
                onClick={() =>
                  downloadJson(messagePayload, 'delete-chain-message.json')
                }
              >
                Download JSON
              </UiButton>
            </>
          )}
        </div>
        <UiButton
          type='button'
          disabled={isSubmitDisabled}
          onClick={form.handleSubmit(values => {
            const messageObject = buildDeleteChainMessage(values)
            submitProposalTx.mutate({
              rawJsonText: JSON.stringify(messageObject, null, 2),
            })
          })}
        >
          {isSubmitting ? 'Submitting…' : 'Submit proposal'}
        </UiButton>
      </div>
    </div>
  )
}
